import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';

import {Spinner} from '../../Spinner';
import {
    METHOD_INCLUDE_ALL_CAMPAIGNS, METHOD_INCLUDE_CAMPAIGN_ON_CONDITION, METHOD_SELECT_CAMPAIGNS_FROM_LIST,
} from '../../business_units/accounts/AccountCampaignsConfiguration';
import {
    WIZARD_METHODS, campaignsInToday, campaignsLeftOutToday, campaignsToList, selectionIsValid,
} from '../campaignsPreview';
import {fetchCampaigns, fetchInactiveCampaigns} from '../lineWizardApi';
import {LeftOutTodayPreview} from '../../elements/LeftOutTodayPreview';

/**
 * «Campañas seleccionadas» de una cuenta (mockup `#/mano/campanas-sel`).
 *
 * El método del clásico con el preview que pidió Pablo: la lista «entran hoy» siempre a la vista, en
 * lenguaje humano, para que nunca quede la duda de qué está mirando la línea.
 *
 * **La elección manual va ADENTRO de este mismo modal**, como una lista con casillas. El wizard de
 * JP abría un segundo modal («Seleccionar campañas») encima de «Configurar cuenta» y el hijo quedaba
 * DEBAJO del padre (reactstrap le da 1050 y el padre tenía 2300): «+ Agregar campañas no responde»,
 * reproducido en app2 el 24/09. Acá no hay modal hijo que pueda quedar tapado.
 */
export function CampaignsSelectionModal({config, configId, allowedMethods, onSave, onClose}) {
    const {t} = useTranslation();
    const [draft, setDraft] = useState(() => ({
        selection_method: WIZARD_METHODS.includes(config.selection_method) ? config.selection_method : METHOD_INCLUDE_ALL_CAMPAIGNS,
        include_containing: config.include_containing || '',
        exclude_containing: config.exclude_containing || '',
        selected_campaigns: (config.selected_campaigns || []).map(String),
    }));
    const [allCampaigns, setAllCampaigns] = useState(null);
    // Las que la base sabe que no están corriendo (`campaigns/inactive`): un `Set` de ids, vacío si
    // no se pudo saber (ahí no se esconde nada).
    const [inactiveIds, setInactiveIds] = useState(null);
    const [listFailed, setListFailed] = useState(false);
    const [showInvalid, setShowInvalid] = useState(false);
    // La lista arranca sin las inactivas (punto 12): `get-campaigns` trae también las pausadas, las
    // finalizadas y las borradas de la plataforma, y en una cuenta con historia eran la mayoría.
    const [showInactive, setShowInactive] = useState(false);
    // Las que se ven siempre: las que venían elegidas y las que se tildaron en esta apertura. Se
    // ACUMULA y no se recalcula con la selección vigente: destildar una no puede sacarla de la lista
    // en el mismo clic (review C-rec-fe-linea-03).
    const [pinned, setPinned] = useState(() => new Set((config.selected_campaigns || []).map(String)));

    const isManual = draft.selection_method === METHOD_SELECT_CAMPAIGNS_FROM_LIST;

    // La lista para elegir a mano sale en vivo de la plataforma (trae también las pausadas), y cuáles
    // no corren, de la base: se piden recién cuando hacen falta, juntas.
    useEffect(() => {
        if (!isManual || allCampaigns !== null) return;
        setListFailed(false);
        Promise.all([fetchCampaigns(config.ad_account_id, configId), fetchInactiveCampaigns(config.ad_account_id)])
            .then(([response, inactiveResponse]) => {
                if (response?.status !== 'OK') {
                    setListFailed(true);
                    return;
                }
                const campaigns = response.all_selectable_campaigns || (response.data && response.data.all_selectable_campaigns) || [];
                const inactive = inactiveResponse?.status === 'OK'
                    ? ((inactiveResponse.data && inactiveResponse.data.all_not_active_campaigns) || []) : [];
                setInactiveIds(new Set(inactive.map(campaign => String(campaign.id))));
                setAllCampaigns(campaigns.map(campaign => ({id: String(campaign.id), name: campaign.name})));
            });
    }, [isManual, allCampaigns, config.ad_account_id, configId]);

    const merged = {...config, ...draft};
    const inToday = campaignsInToday(merged);
    // Se recalcula con cada tecla de la condición: es lo que deja ver, antes de guardar, cuánta plata
    // queda afuera con la regla que se está escribiendo.
    const leftOut = campaignsLeftOutToday(merged);
    const valid = selectionIsValid(merged);
    const listed = campaignsToList({campaigns: allCampaigns, inactiveIds, showInactive, pinned});

    const set = changes => setDraft(current => ({...current, ...changes}));
    const toggleCampaign = id => {
        setPinned(current => new Set(current).add(id));
        set({
            selected_campaigns: draft.selected_campaigns.includes(id)
                ? draft.selected_campaigns.filter(selected => selected !== id)
                : [...draft.selected_campaigns, id],
        });
    };

    const save = () => {
        if (!valid) {
            setShowInvalid(true);
            return;
        }
        onSave({
            selection_method: draft.selection_method,
            include_containing: draft.selection_method === METHOD_INCLUDE_CAMPAIGN_ON_CONDITION ? draft.include_containing.trim() : '',
            exclude_containing: draft.selection_method === METHOD_INCLUDE_CAMPAIGN_ON_CONDITION ? draft.exclude_containing.trim() : '',
            selected_campaigns: isManual ? draft.selected_campaigns : [],
            selected_adgroups: [],
        });
    };

    return (
        <Modal isOpen toggle={onClose} className="lw-modal">
            <ModalHeader toggle={onClose}>
                {t('line_wizard.campaigns_modal.title', {account: config.name, interpolation: {escapeValue: false}})}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    <p className="lw-modal__sub">{t('line_wizard.campaigns_modal.subtitle')}</p>

                    <label className="lw-modal__label" htmlFor="lw-method">{t('line_wizard.campaigns_modal.method_label')}</label>
                    <select id="lw-method" className="form-select" value={draft.selection_method}
                            onChange={event => set({selection_method: event.target.value})}>
                        {WIZARD_METHODS.map(method => (
                            <option key={method} value={method} disabled={allowedMethods && !allowedMethods.includes(method)}>
                                {t(`line_wizard.campaigns_modal.method_${method.toLowerCase()}`)}
                                {allowedMethods && !allowedMethods.includes(method) ? ` · ${t('line_wizard.campaigns_modal.not_in_plan')}` : ''}
                            </option>
                        ))}
                    </select>

                    {draft.selection_method === METHOD_INCLUDE_CAMPAIGN_ON_CONDITION && (
                        <>
                            <div className="lw-modal__row">
                                <div>
                                    <label className="lw-modal__label" htmlFor="lw-include">{t('line_wizard.campaigns_modal.include_label')}</label>
                                    <input id="lw-include" type="text" className="form-control" maxLength={150}
                                           value={draft.include_containing}
                                           onChange={event => set({include_containing: event.target.value})}/>
                                </div>
                                <div>
                                    <label className="lw-modal__label" htmlFor="lw-exclude">{t('line_wizard.campaigns_modal.exclude_label')}</label>
                                    <input id="lw-exclude" type="text" className="form-control" maxLength={150}
                                           placeholder={t('line_wizard.campaigns_modal.optional')}
                                           value={draft.exclude_containing}
                                           onChange={event => set({exclude_containing: event.target.value})}/>
                                </div>
                            </div>
                            <p className="lw-modal__help">{t('line_wizard.campaigns_modal.condition_help')}</p>
                        </>
                    )}

                    {isManual && (
                        <div className="lw-modal__list" role="group" aria-label={t('line_wizard.campaigns_modal.manual_label')}>
                            {listFailed && <p className="lw-modal__error">{t('line_wizard.campaigns_modal.list_error')}</p>}
                            {!listFailed && allCampaigns === null && <div className="box-loader"><Spinner color="positive"/></div>}
                            {allCampaigns !== null && allCampaigns.length === 0 && (
                                <p className="lw-modal__help">{t('line_wizard.campaigns_modal.list_empty')}</p>
                            )}
                            {listed.campaigns.map(campaign => (
                                <label key={campaign.id} className="lw-modal__item">
                                    <input type="checkbox" checked={draft.selected_campaigns.includes(campaign.id)}
                                           onChange={() => toggleCampaign(campaign.id)}/>
                                    <span>{campaign.name}</span>
                                    {/* La etiqueta, sólo en lo que se sabe inactivo: una campaña que
                                        todavía no llegó a la base no es una pausada. */}
                                    {inactiveIds && inactiveIds.has(campaign.id) && (
                                        <span className="lw-tag -muted">{t('line_wizard.campaigns_modal.paused')}</span>
                                    )}
                                </label>
                            ))}
                        </div>
                    )}
                    {/* Fuera de la lista con scroll: al fondo de cientos de campañas no lo veía nadie.
                        No se ofrece si no se sabe qué está inactivo, si no hay inactivas o si son todas
                        (ahí se muestran todas). */}
                    {isManual && listed.canToggle && (
                        <button type="button" className="lw-modal__toggle"
                                onClick={() => setShowInactive(!showInactive)}>
                            {showInactive
                                ? t('line_wizard.campaigns_modal.show_active')
                                : t('line_wizard.campaigns_modal.show_all', {count: listed.hiddenCount})}
                        </button>
                    )}

                    <h3 className="lw-modal__label lw-modal__today">
                        {inToday === null
                            ? t('line_wizard.campaigns_modal.today_unknown')
                            : t('line_wizard.campaigns_modal.today', {count: inToday.length})}
                    </h3>
                    {inToday !== null && inToday.length > 0 && (
                        <ul className="lw-modal__today-list">
                            {inToday.slice(0, 50).map(campaign => (
                                <li key={campaign.id}>
                                    <span>{campaign.name}</span>
                                    <span className="lw-tag -ok">{t('line_wizard.campaigns_modal.active')}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <LeftOutTodayPreview leftOut={leftOut}/>

                    {showInvalid && !valid && (
                        <p className="lw-modal__error" role="alert">
                            {isManual ? t('line_wizard.campaigns_modal.invalid_manual') : t('line_wizard.campaigns_modal.invalid_condition')}
                        </p>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -alt -negative" onClick={onClose}>{t('line_wizard.cancel')}</button>
                <button type="button" className="btn -alt" onClick={save}>{t('line_wizard.campaigns_modal.save')}</button>
            </ModalFooter>
        </Modal>
    );
}
